import React, { useMemo, useState } from "react";
import { Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { PagedTable, SectionCard, StatusChip, formatDateTime, safeArray, toTitleCase } from "../coreNetworkUiShared";

const SCORE_OPTIONS = [1, 2, 3, 4, 5];

const emptyForm = {
  qualityScore: "5",
  communicationScore: "5",
  onTimeScore: "5",
  defectScore: "5",
  overallScore: "5",
  comments: "",
};

export default function CoreNetworkRatingPanel({
  assignments = [],
  ratings = [],
  ratingSummary = null,
  creating = false,
  onCreateRating,
}) {
  const [drafts, setDrafts] = useState({});

  const ratingsByAssignment = useMemo(() => {
    const map = new Map();
    safeArray(ratings).forEach((rating) => {
      if (rating.assignmentId) {
        map.set(Number(rating.assignmentId), rating);
      }
    });
    return map;
  }, [ratings]);

  const completedAssignments = safeArray(assignments).filter(
    (assignment) => String(assignment.status || "").toLowerCase() === "completed"
  );

  const updateDraft = (assignmentId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [assignmentId]: {
        ...emptyForm,
        ...(prev[assignmentId] || {}),
        [field]: value,
      },
    }));
  };

  const getDraft = (assignmentId) => ({
    ...emptyForm,
    ...(ratingsByAssignment.get(Number(assignmentId)) || {}),
    ...(drafts[assignmentId] || {}),
  });

  return (
    <SectionCard title="Ratings" subtitle="Internal provider feedback for completed assignments.">
      <Stack spacing={3}>
        {ratingSummary ? (
          <Grid container spacing={2}>
            <Grid item xs={6} md={4} lg={2}>
              <Typography variant="overline" color="text.secondary">Overall</Typography>
              <Typography variant="h6">{ratingSummary.averageOverallScore ?? "-"}</Typography>
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Typography variant="overline" color="text.secondary">Quality</Typography>
              <Typography variant="h6">{ratingSummary.averageQualityScore ?? "-"}</Typography>
            </Grid>
            <Grid item xs={6} md={4} lg={2}>
              <Typography variant="overline" color="text.secondary">On Time</Typography>
              <Typography variant="h6">{ratingSummary.averageOnTimeScore ?? "-"}</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={2}>
              <Typography variant="overline" color="text.secondary">Communication</Typography>
              <Typography variant="h6">{ratingSummary.averageCommunicationScore ?? "-"}</Typography>
            </Grid>
            <Grid item xs={6} md={6} lg={2}>
              <Typography variant="overline" color="text.secondary">Completed Jobs</Typography>
              <Typography variant="h6">{ratingSummary.completedJobCount ?? 0}</Typography>
            </Grid>
          </Grid>
        ) : null}

        {completedAssignments.length ? (
          <Stack spacing={2}>
            {completedAssignments.map((assignment) => {
              const existingRating = ratingsByAssignment.get(Number(assignment.id)) || null;
              const draft = getDraft(assignment.id);

              return (
                <SectionCard
                  key={assignment.id}
                  title={assignment.member?.displayName || `Assignment #${assignment.id}`}
                  subtitle={assignment.workRequest?.title || toTitleCase(assignment.membership?.role)}
                  noPadding
                >
                  <Stack spacing={2} sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <StatusChip value={assignment.status} />
                      {existingRating ? (
                        <Typography variant="body2" color="text.secondary">
                          Rating saved on {formatDateTime(existingRating.updatedAt || existingRating.createdAt)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Rate Provider
                        </Typography>
                      )}
                    </Stack>

                    <Grid container spacing={2}>
                      {["qualityScore", "communicationScore", "onTimeScore", "defectScore", "overallScore"].map((field) => (
                        <Grid item xs={12} sm={6} md={2.4} key={field}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label={toTitleCase(field.replace("Score", ""))}
                            value={draft[field]}
                            onChange={(event) => updateDraft(assignment.id, field, event.target.value)}
                          >
                            {SCORE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={String(option)}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      ))}
                    </Grid>

                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Comments"
                      value={draft.comments}
                      onChange={(event) => updateDraft(assignment.id, "comments", event.target.value)}
                    />

                    <div>
                      <Button
                        variant="contained"
                        disabled={creating}
                        onClick={() =>
                          onCreateRating?.({
                            assignmentId: assignment.id,
                            ratedMemberId: assignment.memberId,
                            qualityScore: Number(draft.qualityScore),
                            communicationScore: Number(draft.communicationScore),
                            onTimeScore: Number(draft.onTimeScore),
                            defectScore: Number(draft.defectScore),
                            overallScore: Number(draft.overallScore),
                            comments: draft.comments,
                          })
                        }
                      >
                        {existingRating ? "Update Rating" : "Save Rating"}
                      </Button>
                    </div>
                  </Stack>
                </SectionCard>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Rate Provider will appear once an assignment is completed.
          </Typography>
        )}

        <PagedTable
          rows={safeArray(ratings)}
          columns={[
            {
              key: "ratedMember",
              label: "Rated Member",
              render: (row) => row.ratedMember?.displayName || row.ratedMemberId || "-",
            },
            { key: "overallScore", label: "Overall", render: (row) => row.overallScore || "-" },
            { key: "qualityScore", label: "Quality", render: (row) => row.qualityScore || "-" },
            { key: "onTimeScore", label: "On Time", render: (row) => row.onTimeScore || "-" },
            { key: "communicationScore", label: "Communication", render: (row) => row.communicationScore || "-" },
            { key: "defectScore", label: "Defect", render: (row) => row.defectScore || "-" },
            { key: "comments", label: "Comments", render: (row) => row.comments || "-" },
            { key: "createdAt", label: "Saved", render: (row) => formatDateTime(row.updatedAt || row.createdAt) },
          ]}
          emptyTitle="No ratings yet"
          emptyDescription="Completed provider work can be rated here and will feed future matching."
        />
      </Stack>
    </SectionCard>
  );
}
